import React from 'react';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import { getRoundById } from 'general/js/helpers';
import {
    SHOW_STATS_CONFIG_PATH,
    SHOW_R2H_CONFIG_PATH,
    ROOT_CSS_CLASS,
    CELL_DIV_HOLE_ID_PATH,
    CELL_DIV_HOLE_ROUND_PATH
} from './live-player-scores-constants';
import {
    livePlayerClassExtensions,
    livePlayerCellTypes
} from './enums';
import {
    isNullOrUndefined,
    isNumber,
    toggleCssClass,
    getElementsByQuery,
    hasValue,
} from '../../../general/js/general-helpers';
import { PLAYOFF_ROUND_ID } from '../../../general/js/constants';


const getRoundByValue = (roundChoices, value) => {
    if (!Array.isArray(roundChoices) || isNullOrUndefined(value)) return null;

    const foundRound = roundChoices.find((round) => round.value.toString() === value.toString());

    return foundRound || null;
};

export const getAvailableRounds = (scoringPlayer, roundChoices) => {
    if (!hasValue(scoringPlayer)) {
        return [];
    }

    const scoringPlayerRounds = get(scoringPlayer, 'rounds', []);

    if (hasValue(roundChoices)) {
        return cloneDeep(scoringPlayerRounds).filter((round) => {
            return !!getRoundByValue(roundChoices, round.id);
        });
    }

    return scoringPlayerRounds;
};

export const getRoundInfo = (round) => {
    if (!hasValue(round)) {
        return [];
    }

    return get(round, 'info', []);
};

export const getPlayedHolesForRound = (round, usePlayerStrokesProperty) => {
    const roundHoles = getRoundInfo(round);
    const playeHolesForRound = [];

    roundHoles.every((hole) => {
        if (usePlayerStrokesProperty) {
            if (isNumber(hole.playerStrokes) || isNumber(hole.playerPar)) {
                if (hole.playerStrokes !== 0 || hole.playerPar !== 0) {
                    playeHolesForRound.push(hole);
                }
            }
        } else if (isNumber(hole.playerPar)) {
            if (hole.playerPar !== 0) {
                playeHolesForRound.push(hole);
            }
        }

        return true;
    });

    return playeHolesForRound;
};

export const getCountHolesForRound = (round) => {
    const roundHoles = getRoundInfo(round);

    return roundHoles.length;
};

export const getPlayedHoles = (rounds, roundId = null, usePlayerStrokesProperty) => {
    const playedHoles = [];

    if (!isNullOrUndefined(roundId)) {
        return getPlayedHolesForRound(getRoundById(rounds, roundId), usePlayerStrokesProperty);
    }

    rounds.every((round) => {
        const playedHolesForRound = getPlayedHolesForRound(round, usePlayerStrokesProperty);

        if (hasValue(playedHolesForRound)) {
            playedHoles.push(playedHolesForRound);

            return true;
        }
    });

    return playedHoles;
};

export const getLastHole = (playedHoles, roundId = null, usePlayerStrokesProperty) => {
    if (!isNullOrUndefined(roundId)) {
        return getLastHoleForRound(playedHoles, roundId, usePlayerStrokesProperty);
    }

    const lastPlayedRoundHoles = hasValue(playedHoles) ? playedHoles[playedHoles.length - 1] : null;

    const realRoundId = playedHoles.length === 5 ? PLAYOFF_ROUND_ID : playedHoles.length;

    return getLastHoleForRound(lastPlayedRoundHoles, realRoundId, usePlayerStrokesProperty);
};

export const getLastHoleForRound = (roundHoles, roundId, usePlayerStrokesProperty) => {
    return hasValue(roundHoles) ?
        formatHoleDataForClipTag(roundId, roundHoles[roundHoles.length - 1].holeId, getHoleValueBasedOnProperty(usePlayerStrokesProperty, roundHoles[roundHoles.length - 1])) :
        null;
};

export const getCurrentHole = (scoringPlayer, roundChoices, roundId = null, usePlayerStrokesProperty) => {
    const playerRounds = getAvailableRounds(scoringPlayer, roundChoices);
    const playedHoles = getPlayedHoles(playerRounds, roundId, usePlayerStrokesProperty);

    return getLastHole(playedHoles, roundId, usePlayerStrokesProperty);
};

const getHoleValueBasedOnProperty = (usePlayerStrokesProperty, hole) => {
    return usePlayerStrokesProperty ? getFirstPropertyWithValue(hole) : hole.playerPar;
};

const getFirstPropertyWithValue = (hole) => {
    return hole.playerStrokes !== 0 ? hole.playerStrokes : hole.playerPar;
};


export const getFirstHole = (scoringPlayer, roundId = null) => {
    const playerRounds = getAvailableRounds(scoringPlayer);

    if (!isNullOrUndefined(roundId) && hasValue(playerRounds)) {
        const round = getRoundById(playerRounds, roundId);
        const holes = getRoundInfo(round);
        const { holeId, playerPar } = hasValue(holes) ? holes[0] : { holeId: null, playerPar: null };

        return formatHoleDataForClipTag(roundId, holeId, playerPar);
    }

    if (hasValue(playerRounds) && hasValue(playerRounds[0])) {
        const roundHoles = getRoundInfo(playerRounds[0]);
        const { holeId, playerPar } = hasValue(roundHoles) ? roundHoles[0] : { holeId: null, playerPar: null };

        return formatHoleDataForClipTag(playerRounds[0].id, holeId, playerPar);
    }

    return formatHoleDataForClipTag();
};

export const getPlayerScoreCardShowStatsSetting = (props) => {
    const playerScoreCardShowStats = get(props, SHOW_STATS_CONFIG_PATH, false);

    if (!isNullOrUndefined(playerScoreCardShowStats)) {
        return playerScoreCardShowStats;
    }

    return false;
};

export const getPlayerScoreCardShowR2HClipTagSetting = (props) => {
    const playerScoreCardShowClipTag = get(props, SHOW_R2H_CONFIG_PATH, false);

    if (!isNullOrUndefined(playerScoreCardShowClipTag)) {
        return playerScoreCardShowClipTag;
    }

    return false;
};

export const getAvailableChoices = (props) => {
    const { liveScanner, scoringPlayer } = props;
    const roundChoices = get(liveScanner, 'liveComponents.playerScoreCard.roundChoices');

    if (!Array.isArray(scoringPlayer.rounds) || isNullOrUndefined(roundChoices)) return [];

    return cloneDeep(roundChoices).filter((choice) => {
        return !!getRoundById(scoringPlayer.rounds, choice.value);
    });
};

export const getHoleData = (scoringPlayer, roundChoices, roundId = null, usePlayerStrokesProperty) => {
    if (!isNullOrUndefined(roundChoices) && isEmpty(roundChoices)) {
        return {};
    }

    const currentHole = getCurrentHole(scoringPlayer, roundChoices, roundId, usePlayerStrokesProperty);
    const firstHole = getFirstHole(scoringPlayer, roundId);

    return hasValue(currentHole) ? currentHole : hasValue(firstHole) ? firstHole : formatHoleDataForClipTag();
};

export const formatHoleDataForClipTag = (roundId = null, holeId = null, playerPar = null) => {
    return isNumber(holeId) && isNumber(roundId) && isNumber(playerPar) ?
        { round: parseInt(roundId), hole: parseInt(holeId), playerPar: parseInt(playerPar) } :
        { };
};

export const initialiseEventCentreInstance = (clipTag, clipTagParameters) => (state) => {
    const { r2hElement } = state;

    if (hasValue(clipTagParameters) && isNullOrUndefined(r2hElement)) {
        const { eventCentreParams } = clipTagParameters;
        return {
            ...state,
            r2hElement: clipTag.eventCentre(eventCentreParams)
        };
    }

    return {
        ...state
    };
};

export const getCellClassList = (rootClass, type, showClipTag = false, livePlayerCellType) => {
    return classNames({
        'is-wide': type.isWide,
        [`${rootClass}${livePlayerClassExtensions.CELL}`]: livePlayerCellType === livePlayerCellTypes.HOLE_CELL,
        [`${rootClass}${livePlayerClassExtensions.R2HLINK}`]: livePlayerCellType === livePlayerCellTypes.HOLE_CELL && showClipTag,
        [`${rootClass}${livePlayerClassExtensions.PLAYER_CELL}`]: livePlayerCellType === livePlayerCellTypes.PLAYER_CELL,
        [`${rootClass}${livePlayerClassExtensions.IS_CLICKABLE}`]: livePlayerCellType === livePlayerCellTypes.PLAYER_CELL && showClipTag
    });
};

export const setHoleAsActive = (holeId, roundId, holesContainers = { }) => {
    const queryClassName = `.${ROOT_CSS_CLASS}${livePlayerClassExtensions.R2HLINK}`;
    const holeCells = getElementsByQuery(queryClassName, holesContainers);
    holeCells.forEach((cell) => {
        const cellId = parseInt(get(cell, CELL_DIV_HOLE_ID_PATH));
        const cellRound = parseInt(get(cell, CELL_DIV_HOLE_ROUND_PATH));

        const criteria = cellId === holeId && roundId === cellRound;

        toggleCssClass(cell, livePlayerClassExtensions.IS_ACTIVE, criteria);
        toggleCssClass(cell, livePlayerClassExtensions.IS_HOVER, criteria);
    });
};

export const htmlScorecardCellElement = (value, key, classList, attributes) => {
    return <div key={key} className={classList} {...attributes}>{value}</div>;
};

export const holeCellMouseOverHandler = (holeId = '', cellsContainers = {}, isMouseOver = false) => {
    const matchingCells = getElementsByQuery(`div[cell-hole-id="${holeId}"]`, cellsContainers);

    matchingCells.forEach((cell) => {
        toggleCssClass(cell, livePlayerClassExtensions.IS_HOVER, isMouseOver && !cell.classList.contains(livePlayerClassExtensions.IS_ACTIVE));
    });
};
